import React, { useEffect, useState } from 'react';
import Button from '../../../components/common/Button';
import GradientButton from '../../../components/common/GradientButton';
import Input from '../../../components/common/Input';
import JalaliDatePicker from '../../../components/common/JalaliDatePicker';
import Card from '../../../components/common/Card';
import { getMyClubProfileApi, updateMyClubProfileApi } from '../../../api/club.api';
import { toJalaliFull } from '../../../utils/jalali';

export default function ClubProfile() {
  const [club, setClub] = useState(null);
  const [form, setForm] = useState({
    name: '',
    address: '',
    licenseNumber: '',
    phone: '',
    email: '',
    city: '',
    province: '',
    websiteUrl: '',
    instagramUrl: '',
    telegramUrl: '',
    description: '',
    establishmentDate: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getMyClubProfileApi();
        const c = data.data;
        setClub(c);
        setForm({
          name: c.name || '',
          address: c.address || '',
          licenseNumber: c.licenseNumber || '',
          phone: c.phone || '',
          email: c.email || '',
          city: c.city || '',
          province: c.province || '',
          websiteUrl: c.websiteUrl || '',
          instagramUrl: c.instagramUrl || '',
          telegramUrl: c.telegramUrl || '',
          description: c.description || '',
          establishmentDate: c.establishmentDate || '',
        });
      } catch (err) {
        setError(err.response?.data?.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      await updateMyClubProfileApi({
        ...form,
        establishmentDate: form.establishmentDate || undefined,
      });
      setSuccess('اطلاعات باشگاه با موفقیت بروزرسانی شد');
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در بروزرسانی');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-black text-text-dark">پروفایل باشگاه</h1>
        <p className="text-sm text-slate-400">مدیریت اطلاعات و تنظیمات باشگاه</p>
      </div>

      {error && (
        <div className="rounded-xl bg-red-50 p-3 text-sm text-red-600 animate-fade-in">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700 animate-fade-in">
          {success}
        </div>
      )}

      <Card>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="نام باشگاه"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <JalaliDatePicker
              label="تاریخ تاسیس"
              value={form.establishmentDate}
              onChange={(date) => setForm({ ...form, establishmentDate: date })}
            />
            <Input
              label="شماره مجوز"
              value={form.licenseNumber}
              onChange={(e) => setForm({ ...form, licenseNumber: e.target.value })}
            />
            <Input
              label="تلفن باشگاه"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
            <Input
              label="ایمیل باشگاه"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <Input
              label="شهر"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
            <Input
              label="استان"
              value={form.province}
              onChange={(e) => setForm({ ...form, province: e.target.value })}
            />
            <Input
              label="آدرس"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="col-span-2"
            />
          </div>

          <div className="border-t border-slate-200 pt-4">
            <p className="mb-3 font-bold text-text-dark">شبکه‌های اجتماعی و وب‌ سایت</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="وب‌ سایت"
                value={form.websiteUrl}
                onChange={(e) => setForm({ ...form, websiteUrl: e.target.value })}
              />
              <Input
                label="اینستاگرام"
                value={form.instagramUrl}
                onChange={(e) => setForm({ ...form, instagramUrl: e.target.value })}
              />
              <Input
                label="تلگرام"
                value={form.telegramUrl}
                onChange={(e) => setForm({ ...form, telegramUrl: e.target.value })}
              />
              <Input
                label="توضیحات"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="col-span-2"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" type="reset" onClick={() => window.location.reload()}>
              بازنشانی
            </Button>
            <GradientButton type="submit" disabled={submitting}>
              {submitting ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
            </GradientButton>
          </div>
        </form>
      </Card>

      {club && (
        <Card gradient className="border-emerald-200">
          <p className="text-sm text-slate-500">وضعیت باشگاه</p>
          <div className="mt-2 flex items-center gap-2">
            <span className={`h-3 w-3 rounded-full ${club.status === 'ACTIVE' ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
            <span className="font-medium">{club.status === 'ACTIVE' ? 'فعال' : 'در انتظار تایید'}</span>
          </div>
          <p className="mt-1 text-xs text-slate-400">
            تاریخ عضویت: {toJalaliFull(club.createdAt)}
          </p>
        </Card>
      )}
    </div>
  );
}